/*
 * BAKEPACK - Inquiry Tracking V1.3
 * Shopify Galleria / Powerful Form Builder / Tawk.to
 *
 * V1.3 changes:
 * - Separates PFB tracking from Tawk inquiry tracking.
 * - PFB Submit Time is written only to PFB hidden fields.
 * - Tawk Submitting Time is written only after Tawk onPrechatSubmit
 *   or onOfflineSubmit fires.
 * - Ordinary page views, chat opens and chat messages do not
 *   count as Tawk form submissions.
 * - Preserves existing Tawk callbacks.
 * - Avoids duplicate Shopify consent API loading.
 * - Keeps consent checks, 30-day ad attribution,
 *   Beijing time and PFB retry Lead ID.
 *
 * IMPORTANT:
 * - Does not load or duplicate the Tawk embed script.
 * - Tawk form callbacks are not supported in pop-out windows.
 * - Existing verified Tawk attribute keys are retained.
 */

(function () {
  "use strict";

  if (window.__bpInquiryTrackingLoaded) return;
  window.__bpInquiryTrackingLoaded = true;

  const PREFIX = "bp_inquiry_v1_";
  const SESSION_KEY = PREFIX + "session";
  const ADS_KEY = PREFIX + "ads";
  const TAWK_STATE_KEY = PREFIX + "tawk_state";

  const ADS_TTL = 30 * 24 * 60 * 60 * 1000;
  const CONSENT_WITHDRAWN = "Consent withdrawn";
  const NOT_SUBMITTED = "Not submitted";

  const fields = {
    gclid: "bp-gclid-field",
    landing: "bp-landing-page-field",
    submission: "bp-submission-page-field",
    lead: "bp-lead-id-field",
    entry: "bp-entry-time-field",
    submitted: "bp-submit-time-field",
    source: "bp-utm-source-field",
    medium: "bp-utm-medium-field",
    campaign: "bp-utm-campaign-field"
  };

  // Existing verified Tawk attribute keys.
  const TAWK_ANALYTICS_KEYS = [
    "lead_id",
    "landing_page",
    "entry_time",
    "submitting_time"
  ];

  const formStates = new WeakMap();

  let preferredForm = null;
  let lastTawkPayload = "";
  let tawkSyncInFlight = false;
  let tawkSyncQueued = false;
  let tawkHooksInstalled = false;


  // ================================================
  // 1. Safe storage
  // ================================================

  function read(storageName, key) {
    try {
      const storage = window[storageName];

      if (!storage) return null;

      return JSON.parse(
        storage.getItem(key) || "null"
      );

    } catch (_) {
      return null;
    }
  }


  function write(storageName, key, value) {
    try {
      const storage = window[storageName];

      if (storage) {
        storage.setItem(
          key,
          JSON.stringify(value)
        );
      }

    } catch (_) {}
  }


  function remove(storageName, key) {
    try {
      const storage = window[storageName];

      if (storage) {
        storage.removeItem(key);
      }

    } catch (_) {}
  }


  // ================================================
  // 2. Shopify consent
  // ================================================

  function allowed(purpose) {

    const privacy =
      window.Shopify &&
      window.Shopify.customerPrivacy;

    if (!privacy) return false;

    try {

      if (purpose === "marketing") {

        if (
          typeof privacy.marketingAllowed !== "function" ||
          privacy.marketingAllowed() !== true
        ) {
          return false;
        }

        if (
          typeof privacy.saleOfDataRegion !== "function" ||
          typeof privacy.saleOfDataAllowed !== "function"
        ) {
          return false;
        }

        const saleRegion =
          privacy.saleOfDataRegion();

        if (
          saleRegion === true &&
          privacy.saleOfDataAllowed() !== true
        ) {
          return false;
        }

        return true;
      }


      if (purpose === "analytics") {

        return (
          typeof privacy.analyticsProcessingAllowed === "function" &&
          privacy.analyticsProcessingAllowed() === true
        );

      }

    } catch (_) {}

    return false;
  }


  function initializeConsentTracking() {

    const shopify = window.Shopify;

    // Reuse the existing customerPrivacy API.
    // Do not request another version when already available.

    if (
      shopify &&
      shopify.customerPrivacy
    ) {
      privacyChanged();
      return;
    }


    // Only load the API if customerPrivacy is absent.

    if (
      shopify &&
      typeof shopify.loadFeatures === "function"
    ) {

      shopify.loadFeatures(
        [{
          name: "consent-tracking-api",
          version: "0.1"
        }],

        function (error) {

          if (error) {
            console.warn(
              "[Bakepack] Shopify consent API load warning:",
              error
            );
          }

          privacyChanged();

        }
      );

      return;
    }


    // Fail closed if Shopify consent API is unavailable.

    privacyChanged();
  }


  // ================================================
  // 3. Beijing time and Lead ID
  // ================================================

  function pageUrl() {

    return (
      window.location.origin +
      window.location.pathname
    );

  }


  function chinaTime() {

    const parts =
      new Intl.DateTimeFormat("en-GB", {

        timeZone: "Asia/Shanghai",

        year: "numeric",
        month: "2-digit",
        day: "2-digit",

        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",

        hourCycle: "h23"

      }).formatToParts(new Date());


    const values = {};

    parts.forEach(part => {
      values[part.type] = part.value;
    });


    return (
      `${values.year}/${values.month}/${values.day} ` +
      `${values.hour}:${values.minute}:${values.second}`
    );

  }


  function newLeadId() {

    const timestamp =
      chinaTime().replace(/\D/g, "");


    const random =
      window.crypto &&
      typeof window.crypto.randomUUID === "function"

        ? window.crypto.randomUUID()
            .replace(/-/g, "")
            .slice(0, 10)
            .toUpperCase()

        : Math.random()
            .toString(36)
            .slice(2, 12)
            .toUpperCase();


    return "BP-" + timestamp + "-" + random;

  }


  // ================================================
  // 4. Advertising attribution
  // ================================================

  function getValidAdsValues() {

    const record =
      read("localStorage", ADS_KEY);


    if (
      !record ||
      !record.savedAt ||
      Date.now() - record.savedAt > ADS_TTL
    ) {

      if (record) {
        remove("localStorage", ADS_KEY);
      }

      return null;
    }


    return record.values || null;

  }


  function collect() {

    // Analytics: first landing page and entry time.

    if (allowed("analytics")) {

      if (!read("sessionStorage", SESSION_KEY)) {

        write("sessionStorage", SESSION_KEY, {

          landing: pageUrl(),

          entry: chinaTime()

        });

      }

    } else {

      remove("sessionStorage", SESSION_KEY);

      // Do not restore an old Tawk form timestamp
      // after analytics consent is withdrawn.

      const tawkState = getTawkState();

      tawkState.submittingTime = "";

      saveTawkState(tawkState);

    }


    // Marketing attribution.

    if (allowed("marketing")) {

      const params =
        new URLSearchParams(
          window.location.search
        );


      const keys = [
        "gclid",
        "utm_source",
        "utm_medium",
        "utm_campaign"
      ];


      if (keys.some(key => params.has(key))) {

        const values = {};

        keys.forEach(key => {

          values[key] = (
            params.get(key) || ""
          ).slice(0, 2000);

        });


        write("localStorage", ADS_KEY, {

          values: values,

          savedAt: Date.now()

        });

      }


      getValidAdsValues();

    } else {

      remove("localStorage", ADS_KEY);

    }

  }


  // ================================================
  // 5. Powerful Form Builder
  // ================================================

  function getForms() {

    const inputs = Array.from(

      document.querySelectorAll(
        "." + fields.lead + " input"
      )

    );


    return Array.from(

      new Set(

        inputs

          .map(input =>

            input.closest("form") ||

            input.closest(".globo-form-app") ||

            input.closest(".globo-formbuilder")

          )

          .filter(Boolean)

      )

    );

  }


  function getState(form) {

    if (!form) return null;


    if (!formStates.has(form)) {

      formStates.set(form, {

        leadId: newLeadId(),

        submitTime: "",

        lastAttempt: 0

      });

    }


    return formStates.get(form);

  }


  function fill(form, className, value) {

    if (!form) return;


    const input = form.querySelector(
      "." + className + " input"
    );


    if (!input || input.value === value) return;


    const descriptor =
      Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        "value"
      );


    if (descriptor && descriptor.set) {

      descriptor.set.call(input, value);

    } else {

      input.value = value;

    }


    input.dispatchEvent(
      new Event("input", { bubbles: true })
    );


    input.dispatchEvent(
      new Event("change", { bubbles: true })
    );

  }


  function syncForm(form) {

    if (!form) return;


    const state = getState(form);


    const session =
      allowed("analytics")

        ? read("sessionStorage", SESSION_KEY)

        : null;


    const ads =
      allowed("marketing")

        ? getValidAdsValues()

        : null;


    const values = {

      gclid:
        ads && ads.gclid
          ? ads.gclid
          : "",


      landing:
        session && session.landing
          ? session.landing
          : "",


      submission:
        pageUrl(),


      lead:
        state.leadId,


      entry:
        session && session.entry
          ? session.entry
          : "",


      submitted:
        state.submitTime,


      source:
        ads && ads.utm_source
          ? ads.utm_source
          : "",


      medium:
        ads && ads.utm_medium
          ? ads.utm_medium
          : "",


      campaign:
        ads && ads.utm_campaign
          ? ads.utm_campaign
          : ""

    };


    Object.keys(fields).forEach(key => {

      fill(
        form,
        fields[key],
        values[key]
      );

    });

  }


  // ================================================
  // 6. Tawk.to visitor attributes
  // ================================================

  function getTawkState() {

    const saved =
      read("sessionStorage", TAWK_STATE_KEY);


    if (
      saved &&
      typeof saved === "object"
    ) {

      return {

        analyticsSent:
          saved.analyticsSent === true,

        marketingSent:
          saved.marketingSent === true,

        submittingTime:
          typeof saved.submittingTime === "string"
            ? saved.submittingTime
            : ""

      };

    }


    return {

      analyticsSent: false,

      marketingSent: false,

      submittingTime: ""

    };

  }


  function saveTawkState(state) {

    write(
      "sessionStorage",
      TAWK_STATE_KEY,
      state
    );

  }


  function updateTawkStateAfterSuccess(attributes) {

    const state = getTawkState();


    const analyticsValuesSent =
      TAWK_ANALYTICS_KEYS.some(key =>

        attributes[key] &&
        attributes[key] !== CONSENT_WITHDRAWN

      );


    const analyticsCleared =
      TAWK_ANALYTICS_KEYS.some(key =>

        attributes[key] === CONSENT_WITHDRAWN

      );


    if (analyticsValuesSent) {
      state.analyticsSent = true;
    }


    if (analyticsCleared) {
      state.analyticsSent = false;
    }


    if (
      attributes.gclid &&
      attributes.gclid !== CONSENT_WITHDRAWN
    ) {

      state.marketingSent = true;

    }


    if (
      attributes.gclid === CONSENT_WITHDRAWN ||
      attributes.gclid === "No GCLID"
    ) {

      state.marketingSent = false;

    }


    saveTawkState(state);

  }


  function sendTawkAttributes(rawAttributes) {

    const api = window.Tawk_API;


    if (
      !api ||
      typeof api.setAttributes !== "function"
    ) {
      return;
    }


    const attributes = {};


    Object.keys(rawAttributes).forEach(key => {

      const rawValue = rawAttributes[key];


      if (
        rawValue === null ||
        rawValue === undefined
      ) {
        return;
      }


      const value = String(rawValue).trim();


      if (!value) return;


      attributes[key] = value.slice(0, 255);

    });


    if (!Object.keys(attributes).length) return;


    const signature =
      JSON.stringify(attributes);


    if (signature === lastTawkPayload) return;


    if (tawkSyncInFlight) {

      tawkSyncQueued = true;

      return;

    }


    tawkSyncInFlight = true;


    try {

      api.setAttributes(

        attributes,

        function (error) {

          tawkSyncInFlight = false;


          if (error) {

            console.warn(
              "[Bakepack] Tawk attribute sync failed:",
              error
            );

          } else {

            lastTawkPayload = signature;

            updateTawkStateAfterSuccess(
              attributes
            );


            console.info(
              "[Bakepack] Tawk visitor attributes synced."
            );

          }


          if (tawkSyncQueued) {

            tawkSyncQueued = false;

            syncTawk(preferredForm);

          }

        }

      );

    } catch (error) {

      tawkSyncInFlight = false;

      console.warn(
        "[Bakepack] Tawk attribute sync error:",
        error
      );

    }

  }


  function syncTawk(form) {

    const api = window.Tawk_API;


    if (
      !api ||
      typeof api.setAttributes !== "function"
    ) {
      return;
    }


    const analyticsAllowed =
      allowed("analytics");


    const marketingAllowed =
      allowed("marketing");


    const tawkState =
      getTawkState();


    const attributes = {};


    const targetForm =
      form ||
      preferredForm ||
      getForms()[0] ||
      null;


    if (analyticsAllowed) {

      const session =
        read("sessionStorage", SESSION_KEY);


      const formState =
        targetForm
          ? getState(targetForm)
          : null;


      if (
        formState &&
        formState.leadId
      ) {

        attributes.lead_id =
          formState.leadId;

      }


      if (
        session &&
        session.landing
      ) {

        attributes.landing_page =
          session.landing;

      }


      if (
        session &&
        session.entry
      ) {

        attributes.entry_time =
          session.entry;

      }


      // IMPORTANT:
      // This is Tawk's own form timestamp.
      // It never reads PFB state.submitTime.

      attributes.submitting_time =
        tawkState.submittingTime ||
        NOT_SUBMITTED;

    } else if (tawkState.analyticsSent) {

      TAWK_ANALYTICS_KEYS.forEach(key => {

        attributes[key] =
          CONSENT_WITHDRAWN;

      });

    }


    if (marketingAllowed) {

      const ads =
        getValidAdsValues();


      if (
        ads &&
        ads.gclid
      ) {

        attributes.gclid =
          ads.gclid;

      } else if (tawkState.marketingSent) {

        attributes.gclid =
          "No GCLID";

      }

    } else if (tawkState.marketingSent) {

      attributes.gclid =
        CONSENT_WITHDRAWN;

    }


    sendTawkAttributes(attributes);

  }


  // ================================================
  // 7. Tawk's own inquiry form events
  // ================================================

  function handleTawkInquirySubmit(formType, data) {

    if (!allowed("analytics")) {

      console.info(
        "[Bakepack] Tawk form submitted, but analytics consent is not granted; timestamp not stored."
      );

      return;

    }


    const state =
      getTawkState();


    // Keep the first Tawk form submission time
    // for this tab session.

    if (!state.submittingTime) {

      state.submittingTime =
        chinaTime();

      saveTawkState(state);

    }


    console.info(
      "[Bakepack] Tawk inquiry form submitted:",
      formType
    );


    syncTawk(preferredForm);

  }


  // ================================================
  // 8. Preserve existing Tawk callbacks
  // ================================================

  function wrapTawkCallback(
    api,
    callbackName,
    handler
  ) {

    const existing =
      api[callbackName];


    if (
      existing &&
      existing.__bpInquiryTrackingWrapper === true
    ) {
      return;
    }


    const wrapped = function () {

      const args = arguments;


      // Run the original callback first.

      if (typeof existing === "function") {

        try {

          existing.apply(this, args);

        } catch (error) {

          console.warn(
            "[Bakepack] Existing Tawk " +
            callbackName +
            " callback failed:",
            error
          );

        }

      }


      // Then run Bakepack tracking.

      try {

        handler.apply(this, args);

      } catch (error) {

        console.warn(
          "[Bakepack] Tawk " +
          callbackName +
          " tracking callback failed:",
          error
        );

      }

    };


    wrapped.__bpInquiryTrackingWrapper = true;

    wrapped.__bpOriginalCallback = existing;


    api[callbackName] = wrapped;

  }


  function installTawkHooks() {

    const api = window.Tawk_API;


    if (
      !api ||
      typeof api !== "object"
    ) {
      return false;
    }


    if (!tawkHooksInstalled) {

      try {

        // Preserve the existing onLoad callback.

        wrapTawkCallback(
          api,
          "onLoad",
          function () {

            syncTawk(preferredForm);

          }
        );


        // Tawk Pre-Chat Form submission.

        wrapTawkCallback(
          api,
          "onPrechatSubmit",
          function (data) {

            handleTawkInquirySubmit(
              "prechat",
              data
            );

          }
        );


        // Tawk Offline Form submission.

        wrapTawkCallback(
          api,
          "onOfflineSubmit",
          function (data) {

            handleTawkInquirySubmit(
              "offline",
              data
            );

          }
        );


        tawkHooksInstalled = true;


      } catch (error) {

        console.warn(
          "[Bakepack] Could not attach Tawk callbacks:",
          error
        );

        return false;

      }

    }


    // Tawk may already be loaded.

    if (
      typeof api.setAttributes === "function"
    ) {

      syncTawk(preferredForm);

      return true;

    }


    return false;

  }


  function watchForTawk() {

    if (installTawkHooks()) return;


    let attempts = 0;


    const timer = window.setInterval(

      function () {

        attempts++;


        if (
          installTawkHooks() ||
          attempts >= 60
        ) {

          window.clearInterval(timer);


          if (!tawkHooksInstalled) {

            console.warn(
              "[Bakepack] Tawk API was not found; tracking hooks were not installed."
            );

          }

        }

      },

      500

    );

  }


  // ================================================
  // 9. PFB submit detection
  // ================================================

  function beforeSubmit(form) {

    if (
      !form ||
      !form.querySelector(
        "." + fields.lead + " input"
      )
    ) {
      return;
    }


    preferredForm = form;


    const state =
      getState(form);


    const now =
      Date.now();


    // Avoid duplicate click + native submit handling.

    if (
      now - state.lastAttempt < 300
    ) {
      return;
    }


    // PFB timestamp is used only by PFB.
    // Failed retries retain the same Lead ID.

    state.submitTime =
      chinaTime();

    state.lastAttempt =
      now;


    syncForm(form);


    // Refresh visitor metadata.
    // Tawk submitting_time is NOT taken from PFB.

    syncTawk(form);

  }


  // Detect PFB submit-button clicks.

  document.addEventListener(

    "click",

    function (event) {

      const target =
        event.target;


      if (
        !target ||
        typeof target.closest !== "function"
      ) {
        return;
      }


      const button =
        target.closest(
          "button, input[type='submit']"
        );


      if (!button) return;


      const label = (

        button.value ||
        button.textContent ||
        ""

      ).trim();


      const isSubmit =

        button.matches("[type='submit']") ||

        /^(request a quote|submit|send inquiry|send message)$/i
          .test(label);


      if (!isSubmit) return;


      const form =

        button.closest("form") ||

        button.closest(".globo-form-app") ||

        button.closest(".globo-formbuilder");


      if (form) {

        beforeSubmit(form);

      }

    },

    true

  );


  // Detect native PFB form submission.

  document.addEventListener(

    "submit",

    function (event) {

      const form =

        event.target &&
        event.target.closest

          ? event.target.closest("form")

          : null;


      if (form) {

        beforeSubmit(form);

      }

    },

    true

  );


  // Remember the PFB form currently being used.

  document.addEventListener(

    "focusin",

    function (event) {

      const target =
        event.target;


      if (
        !target ||
        typeof target.closest !== "function"
      ) {
        return;
      }


      const form =

        target.closest("form") ||

        target.closest(".globo-form-app");


      if (

        form &&

        form.querySelector(
          "." + fields.lead + " input"
        )

      ) {

        preferredForm = form;

      }

    },

    true

  );


  // ================================================
  // 10. Consent updates
  // ================================================

  function privacyChanged() {

    collect();

    syncAll();

  }


  document.addEventListener(

    "visitorConsentCollected",

    privacyChanged

  );


  // ================================================
  // 11. Initial sync and dynamic forms
  // ================================================

  function syncAll() {

    const forms =
      getForms();


    forms.forEach(syncForm);


    if (
      !preferredForm &&
      forms.length
    ) {

      preferredForm = forms[0];

    }


    syncTawk(

      preferredForm ||
      forms[0] ||
      null

    );

  }


  let scheduled = false;


  function scheduleSync() {

    if (scheduled) return;


    scheduled = true;


    window.requestAnimationFrame(

      function () {

        scheduled = false;

        syncAll();

      }

    );

  }


  if (
    document.readyState === "loading"
  ) {

    document.addEventListener(

      "DOMContentLoaded",

      scheduleSync,

      { once: true }

    );

  } else {

    scheduleSync();

  }


  const observer =
    new MutationObserver(scheduleSync);


  function observe() {

    if (!document.body) return;


    observer.observe(

      document.body,

      {

        childList: true,

        subtree: true

      }

    );


    scheduleSync();

  }


  if (document.body) {

    observe();

  } else {

    document.addEventListener(

      "DOMContentLoaded",

      observe,

      { once: true }

    );

  }


  // ================================================
  // 12. Initialize
  // ================================================

  // Shopify consent API.

  initializeConsentTracking();


  // Existing Tawk app embed only.
  // Never inject a second Tawk script.

  watchForTawk();


})();